import {
  Box,
  Button,
  H3,
  Stack,
  Text,
  Badge,
  Spinner
} from "@adfenix/alexandria";
import { ReactNode, useEffect } from "react";
import { FacebookIntegration } from "api/services/facebookService";
import cx from "classnames";
import PageItem from "./PageItem";
import { StyledLevel, StyledLevelItem } from "./StyledElements";
import { useTranslation } from "react-i18next";
import { PageInfo } from "./interfaces";
import ErrorMessageWithTitle from "./ErrorMessageWithTitle";
import { useQuery } from "@tanstack/react-query";
import { getFacebookPagePromotionStatus } from "api/services/facebookService";
import Popover from "components/Popover";
import { FacebookService } from "@adfenix/types";

const PageConnected = ({
  hqFacebookIntegration,
  officeFacebookIntegration,
  verifiedFacebookAccount,
  loadingVerifiedFacebookAccount,
  handleLogin,
  errorMessageTitle,
  errorMessageBody
}: {
  hqFacebookIntegration: FacebookIntegration;
  officeFacebookIntegration: FacebookIntegration | undefined;
  verifiedFacebookAccount: FacebookService.ProfileDetail | undefined;
  loadingVerifiedFacebookAccount: boolean;
  handleLogin: () => void;
  errorMessageTitle: string | undefined;
  errorMessageBody: string | undefined;
}) => {
  const { t } = useTranslation();

  const {
    data: promotionStatus,
    isFetching: isLoadingPromotionStatus,
    refetch
  } = useQuery(
    ["getPromotionStatus"],
    () =>
      getFacebookPagePromotionStatus({
        businessMangerId: hqFacebookIntegration.businessManagerId ?? "",
        pageUrl: encodeURIComponent(
          getTheProperDataFallbackToHqData(
            officeFacebookIntegration,
            hqFacebookIntegration
          ).facebook.facebookPageUrl ?? ""
        )
      }),
    {
      retry: false,
      enabled: Boolean(verifiedFacebookAccount?.id)
    }
  );

  const getTheProperDataFallbackToHqData = (
    officIntegration: FacebookIntegration | undefined,
    hqIntegration: FacebookIntegration
  ): PageInfo => {
    if (officIntegration)
      return {
        facebook: {
          facebookPageName: officIntegration.facebookPageName,
          facebookPageUrl: officIntegration.facebookPageUrl,
          facebookProfilePicture: officIntegration.facebookProfileImage,
          accessToken: "",
          facebookPageId: officIntegration.facebookPageId
        },
        instagram: {
          instagramAccountName: officIntegration.instagramPageName,
          instagramActorId: officIntegration.instagramActorId,
          instagramProfilePicture: officIntegration.instagramProfileImage
        }
      };
    else
      return {
        facebook: {
          facebookPageName: hqIntegration.facebookPageName,
          facebookPageUrl: hqIntegration.facebookPageUrl,
          facebookProfilePicture: hqIntegration.facebookProfileImage,
          accessToken: "",
          facebookPageId: hqIntegration.facebookPageId
        },
        instagram: {
          instagramAccountName: hqIntegration.instagramPageName,
          instagramActorId: hqIntegration.instagramActorId,
          instagramProfilePicture: hqIntegration.instagramProfileImage
        }
      };
  };

  useEffect(() => {
    if (
      (hqFacebookIntegration ||
        verifiedFacebookAccount ||
        officeFacebookIntegration) &&
      verifiedFacebookAccount?.id
    )
      refetch();
  }, [
    hqFacebookIntegration,
    verifiedFacebookAccount,
    officeFacebookIntegration,
    verifiedFacebookAccount?.id,
    refetch
  ]);

  return (
    <Stack spacing="l">
      <H3 style={{ marginBottom: 8 }}>
        {t("selfonboarding.pageConnected.header")}
      </H3>

      <Box display="block" justifyContent="flex-start">
        {!loadingVerifiedFacebookAccount && verifiedFacebookAccount?.id ? (
          <>
            <Badge color="success">
              {t("selfonboarding.pageConnected.connected.badge")}
            </Badge>{" "}
            <WithSpinnerFallback isLoading={isLoadingPromotionStatus}>
              {promotionStatus && promotionStatus?.eligibleForPromotion ? (
                <Badge color="success">
                  {t("selfonboarding.pageConnected.promotion.eligible")}
                </Badge>
              ) : (
                <Popover
                  style={{ overflowWrap: "break-word" }}
                  trigger={
                    <Badge color="danger">
                      {t("selfonboarding.pageConnected.promotion.not.eligible")}
                    </Badge>
                  }
                >
                  {promotionStatus?.promotionIneligibleReason}
                </Popover>
              )}
            </WithSpinnerFallback>
          </>
        ) : (
          <WithSpinnerFallback isLoading={loadingVerifiedFacebookAccount}>
            <Badge color="danger">
              {t("selfonboarding.pageConnected.connectionFailed.badge")}
            </Badge>
          </WithSpinnerFallback>
        )}
      </Box>

      <Text>{t("selfonboarding.pageConnected.description")}</Text>

      <Box>
        <StyledLevel>
          <StyledLevelItem style={{ marginTop: 16 }} className={cx("checked")}>
            <PageItem
              pageData={getTheProperDataFallbackToHqData(
                officeFacebookIntegration,
                hqFacebookIntegration
              )}
            />
          </StyledLevelItem>
        </StyledLevel>
      </Box>
      <ErrorMessageWithTitle
        title={errorMessageTitle}
        body={errorMessageBody}
      />
      <Box display="flex" justifyContent="start">
        <Button
          logLabel="page-connected-edit-button"
          type="button"
          variant={verifiedFacebookAccount?.id ? "secondary" : "primary"}
          onClick={handleLogin}
        >
          {verifiedFacebookAccount?.id
            ? t("selfonboarding.pageConnected.edit.button")
            : t("selfonboarding.pageConnected.reconnect.button")}
        </Button>
      </Box>
    </Stack>
  );
};

const WithSpinnerFallback = ({
  isLoading,
  children
}: {
  isLoading: boolean;
  children: ReactNode;
}) => {
  if (isLoading) {
    return <Spinner size={20} />;
  }

  return <>{children}</>;
};

export default PageConnected;
